import { Flex, FlexGap, IconButton, Text, useModal } from '@pancakeswap/uikit'
import React, { useCallback, useEffect, useRef, useState } from 'react'

// import { shouldShowMMLiquidityError } from 'views/Swap/MMLinkPools/utils/exchange'
// import { MMLiquidityWarning } from 'views/Swap/MMLinkPools/components/MMLiquidityWarning'
import { Currency } from '@pancakeswap/sdk'
import { useUserSlippagePercent } from '@pancakeswap/utils/user'
import SettingsModal from 'components/EMenu/GlobalSettings/SettingsModal'
import { SettingsMode } from 'components/EMenu/GlobalSettings/types'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import styled, { css, keyframes } from 'styled-components'
import { Field } from 'state/swap/actions'
import { useSwapState } from 'state/swap/hooks'
import { useDerivedBestTradeWithMM } from '../MMLinkPools/hooks/useDerivedSwapInfoWithMM'
import {
  FormMain,
  MMTradeDetail,
  SwapCommitButton,
  TradeDetails
} from './containers'
import { MMCommitButton } from './containers/MMCommitButton'
import { useSwapBestTrade } from './hooks'

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const rotateAnimation = () =>
  css`
    ${rotate} 0.5s linear infinite;
  `

type V3SwapFormProps = {
  inputCurrencyId?: string
  inputCurrency?: Currency
  outputCurrencyId?: string
  outputCurrency?: Currency
  isChartExpanded?: boolean
  setIsChartExpanded?: React.Dispatch<React.SetStateAction<boolean>>
  isChartDisplayed?: boolean
  currentSwapPrice?: {
    [key: string]: number
  }
  isMobile?: boolean
  isFullWidthContainer?: boolean
  setIsChartDisplayed?: React.Dispatch<React.SetStateAction<boolean>>
}

const SettingsButton = styled(FlexGap)`
  cursor: pointer;
  &:hover {
    opacity: 0.65;
  }
`

const RefreshButton = styled(FlexGap)<{ animate: boolean }>`
  cursor: pointer;
  animation: ${({ animate }) => animate ? rotateAnimation : ""};
  &:hover {
    opacity: 0.65;
  }
`

export const V3SwapForm: React.FC<React.PropsWithChildren<V3SwapFormProps>> = () => {
  const {
      independentField,
      typedValue,
      [Field.INPUT]: { currencyId: inputCurrencyId },
      [Field.OUTPUT]: { currencyId: outputCurrencyId },
  } = useSwapState()
  const { isLoading, trade, refresh, syncing, isStale, error } = useSwapBestTrade()
  const mm = useDerivedBestTradeWithMM(trade)

  const [isStarted, setIsStarted] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const animateRef = useRef(null)
  // const throttledHandleRefresh = useMemo(
  //   () =>
  //     throttle(() => {
  //       refresh()
  //     }, 3000),
  //   [refresh],
  // )

  const [slippage] = useUserSlippagePercent()

  const finalTrade = mm.isMMBetter ? mm?.mmTradeInfo?.trade : trade

  const tradeLoaded = !isLoading && !isRefreshing

  useEffect(() => {
    animateRef.current?.endElement()
  }, [inputCurrencyId, outputCurrencyId])

  useEffect(() => {
    if (!isStarted && !isLoading && !!trade && !error) {
      setIsStarted(true)
      animateRef.current?.beginElement()
    }
  }, [error, isLoading, isStarted, trade])

  useEffect(() => {
    if (isRefreshing && !syncing && !error) {
      animateRef.current?.beginElement()
      setIsRefreshing(false)
    }
  }, [error, isRefreshing, syncing])

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true)
    refresh()
  }, [refresh])

  useEffect(() => {
    const animateEl = animateRef.current;
    // setIsStarted(false)
    if (animateEl) {
      animateEl.addEventListener('endEvent', handleRefresh);
    }
    return () => {
      if (animateEl) {
        animateEl.removeEventListener('endEvent', handleRefresh)
      }
    };
  }, [animateRef, handleRefresh])

  // const insufficientFundCurrency = useCheckInsufficientError(trade)
  const [onPresentSettingsModal] = useModal(<SettingsModal mode={SettingsMode.SWAP_LIQUIDITY} />)
  // const { isMobile, isTablet, isDesktop } = useMatchBreakpoints()
  return (
    <>
      <FlexGap gap="8px" justifyContent="flex-end" alignItems="center" pt={2} mb={[1, 3]} px={2}>
        <SettingsButton gap="8px" alignItems="center" onClick={onPresentSettingsModal}>
          <Text color="#A39CB1" fontSize={12}>Slippage {Number(slippage.toFixed(2))}%</Text>
          <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.50036 3.13175C6.50036 2.32252 7.15637 1.6665 7.96561 1.6665H10.0351C10.8443 1.6665 11.5004 2.32252 11.5004 3.13176C11.5004 4.25972 12.7214 4.96468 13.6983 4.4007C14.3991 3.99607 15.2953 4.23619 15.6999 4.93702L16.7346 6.72921C17.1392 7.43004 16.8991 8.32619 16.1983 8.73081C15.2214 9.2948 15.2214 10.7048 16.1983 11.2687C16.8991 11.6734 17.1392 12.5695 16.7346 13.2703L15.6999 15.0625C15.2953 15.7634 14.3991 16.0035 13.6983 15.5989C12.7214 15.0349 11.5004 15.7399 11.5004 16.8679C11.5004 17.6771 10.8443 18.3332 10.0351 18.3332H7.96565C7.15639 18.3332 6.50036 17.6771 6.50036 16.8679C6.50036 15.7399 5.2793 15.0349 4.30245 15.5989C3.60163 16.0035 2.70549 15.7634 2.30086 15.0626L1.26613 13.2704C0.86151 12.5695 1.10163 11.6734 1.80246 11.2687C2.77931 10.7048 2.77931 9.2948 1.80246 8.73081C1.10163 8.32619 0.86151 7.43004 1.26614 6.72921L2.30086 4.93701C2.70549 4.23618 3.60164 3.99607 4.30246 4.40069C5.2793 4.96466 6.50036 4.2597 6.50036 3.13175Z" stroke="#A39CB1" strokeWidth="2" />
            <path d="M12.3337 9.99984C12.3337 11.8408 10.8413 13.3332 9.00035 13.3332C7.15941 13.3332 5.66702 11.8408 5.66702 9.99984C5.66702 8.15889 7.15941 6.6665 9.00035 6.6665C10.8413 6.6665 12.3337 8.15889 12.3337 9.99984Z" stroke="#A39CB1" strokeWidth="2" />
          </svg>
        </SettingsButton>
        <RefreshButton onClick={() => animateRef.current?.endElement()} animate={isRefreshing}>
          <svg viewBox="0 0 24 24" width="28" color="text" xmlns="http://www.w3.org/2000/svg">
            <path stroke="none" fill="var(--colors-primary)" d="M16.2751 7.78995C13.932 5.44681 10.133 5.44681 7.78986 7.78995C7.02853 8.55128 6.51457 9.4663 6.24798 10.4351C6.24473 10.4499 6.24114 10.4646 6.23719 10.4793C6.17635 10.7064 6.12938 10.9339 6.09577 11.161C5.83159 12.9457 6.39255 14.7026 7.52624 15.9944C7.61054 16.0901 7.69842 16.1838 7.78986 16.2752C8.08307 16.5685 8.39909 16.825 8.7322 17.0448C9.25533 17.3892 9.84172 17.6568 10.4798 17.8278C10.7386 17.8971 10.9979 17.9484 11.2565 17.9825C12.9537 18.2061 14.6187 17.6866 15.8747 16.6415C16.0123 16.5265 16.1459 16.4044 16.2751 16.2752C16.2848 16.2655 16.2947 16.2561 16.3047 16.2469C17.0123 15.531 17.5491 14.627 17.8283 13.5851C17.9712 13.0517 18.5196 12.7351 19.053 12.878C19.5865 13.021 19.9031 13.5693 19.7602 14.1028C19.3141 15.7676 18.3745 17.1684 17.1409 18.1899C16.1883 18.9822 15.0949 19.5189 13.9515 19.8002C11.8607 20.3147 9.6028 19.9749 7.7328 18.7809C7.06855 18.3579 6.47841 17.8432 5.97519 17.2589C5.12341 16.2738 4.55173 15.1302 4.26015 13.9324C4.01698 12.9416 3.96104 11.8931 4.12168 10.8379C4.36697 9.20484 5.1183 7.63309 6.37564 6.37574C9.49984 3.25154 14.5652 3.25154 17.6894 6.37574L18.2332 6.91959L18.2337 5.49951C18.2338 5.05769 18.5921 4.69964 19.034 4.69979C19.4758 4.69995 19.8338 5.05825 19.8337 5.50007L19.8325 9.03277L19.8322 9.8325L19.0325 9.83249L18.9401 9.83249C18.8146 9.85665 18.6854 9.85665 18.5599 9.83248L15.5005 9.83245C15.0587 9.83245 14.7005 9.47427 14.7005 9.03244C14.7005 8.59062 15.0587 8.23245 15.5005 8.23245L16.7176 8.23246L16.2751 7.78995Z" />
            <defs>
              <path id="arrow" stroke="none" fill="none" d="M16.2751 7.78995C13.932 5.44681 10.133 5.44681 7.78986 7.78995C7.02853 8.55128 6.51457 9.4663 6.24798 10.4351C6.24473 10.4499 6.24114 10.4646 6.23719 10.4793C6.17635 10.7064 6.12938 10.9339 6.09577 11.161C5.83159 12.9457 6.39255 14.7026 7.52624 15.9944C7.61054 16.0901 7.69842 16.1838 7.78986 16.2752C8.08307 16.5685 8.39909 16.825 8.7322 17.0448C9.25533 17.3892 9.84172 17.6568 10.4798 17.8278C10.7386 17.8971 10.9979 17.9484 11.2565 17.9825C12.9537 18.2061 14.6187 17.6866 15.8747 16.6415C16.0123 16.5265 16.1459 16.4044 16.2751 16.2752C16.2848 16.2655 16.2947 16.2561 16.3047 16.2469C17.0123 15.531 17.5491 14.627 17.8283 13.5851C17.9712 13.0517 18.5196 12.7351 19.053 12.878C19.5865 13.021 19.9031 13.5693 19.7602 14.1028C19.3141 15.7676 18.3745 17.1684 17.1409 18.1899C16.1883 18.9822 15.0949 19.5189 13.9515 19.8002C11.8607 20.3147 9.6028 19.9749 7.7328 18.7809C7.06855 18.3579 6.47841 17.8432 5.97519 17.2589C5.12341 16.2738 4.55173 15.1302 4.26015 13.9324C4.01698 12.9416 3.96104 11.8931 4.12168 10.8379C4.36697 9.20484 5.1183 7.63309 6.37564 6.37574C9.49984 3.25154 14.5652 3.25154 17.6894 6.37574L18.2332 6.91959L18.2337 5.49951C18.2338 5.05769 18.5921 4.69964 19.034 4.69979C19.4758 4.69995 19.8338 5.05825 19.8337 5.50007L19.8325 9.03277L19.8322 9.8325L19.0325 9.83249L18.9401 9.83249C18.8146 9.85665 18.6854 9.85665 18.5599 9.83248L15.5005 9.83245C15.0587 9.83245 14.7005 9.47427 14.7005 9.03244C14.7005 8.59062 15.0587 8.23245 15.5005 8.23245L16.7176 8.23246L16.2751 7.78995Z" />
              <clipPath id="arrow-clip">
                <use xlinkHref="#arrow" />
              </clipPath>
            </defs>
            <g clipPath="url(#arrow-clip)">
              <circle cx="12" cy="12" r="5" transform="rotate(365,12,12)" fill="none" stroke="var(--colors-textSubtle)" strokeWidth="16" strokeDasharray="30" strokeDashoffset="0">
              <animate ref={animateRef} attributeName="stroke-dashoffset" values="0;-30" repeatCount="1" dur="20s" begin="indefinite" />
            </circle>
            </g>
          </svg>
        </RefreshButton>
      </FlexGap>
      <FormMain
        tradeLoading={mm.isMMBetter ? false : !tradeLoaded}
        pricingAndSlippage={
          mm.isMMBetter ? (
            <MMTradeDetail loaded={!mm.mmOrderBookTrade.isLoading} mmTrade={mm.mmTradeInfo} />
          ) : (
            <TradeDetails loaded={tradeLoaded} trade={trade} />
          )
        }
        inputAmount={finalTrade?.inputAmount}
        outputAmount={finalTrade?.outputAmount}
        swapCommitButton={
          mm?.isMMBetter ? (
            <MMCommitButton {...mm} />
          ) : (
            <SwapCommitButton trade={trade} tradeError={error} tradeLoading={!tradeLoaded} onDone={refresh} />
          )
        }
      />
    </>
  )
}
